import { ReactNode, useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { CollegeChip } from '@backrs_ui/grow-ui/src/components/MyColleges/CollegeChip';
import { useDebounce } from '@backrs_ui/grow-ui/src/hooks/useDebounce';
import {
  AutocompleteOption,
  useFetchAutocompleteOptionsQuery,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import type {
  CollegePick,
  UserCollege,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Autocomplete, Box, SxProps, TextField, Theme, Typography } from '@mui/material';

const SCHOOLS_LOOKUP_ENDPOINT = '/profile/lookups/schools';
const MIN_SEARCH_LENGTH = 3;

type CollegePickerProps = Readonly<{
  idPrefix: string;
  colleges: readonly UserCollege[];
  picked: readonly CollegePick[];
  onPickedChange: (picked: readonly CollegePick[]) => void;
  onSubmit?: () => void;
  strings: Readonly<{
    label: string;
    placeholder: string;
    noMatches: string;
    lookupFailed: string;
    onList: string;
    full?: string;
  }>;
  chipsInInput?: boolean;
  disableListed?: boolean;
  disabled?: boolean;
  max?: number;
  searchSx?: SxProps<Theme>;
  children?: ReactNode;
}>;

export const CollegePicker = ({
  idPrefix,
  colleges,
  picked,
  onPickedChange,
  onSubmit,
  strings,
  chipsInInput = false,
  disableListed = false,
  disabled = false,
  max,
  searchSx,
  children,
}: CollegePickerProps) => {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const searchTerm = useDebounce(input.trim(), 300);
  const isFull = max !== undefined && picked.length >= max;
  const {
    currentData: lookup,
    isFetching,
    isError,
  } = useFetchAutocompleteOptionsQuery(
    {
      endpoint: SCHOOLS_LOOKUP_ENDPOINT,
      searchTerm,
      displayProperty: 'school',
      filter: 'post_secondary',
      minSearchLength: MIN_SEARCH_LENGTH,
    },
    { skip: isFull || searchTerm.length < MIN_SEARCH_LENGTH },
  );

  const listed = new Map(colleges.map((college) => [college.institution_id, college]));
  const toPick = (option: AutocompleteOption): CollegePick | null =>
    'school' in option && option.institution_id
      ? {
          institution_id: option.institution_id,
          name: option.name ?? option.school,
          logo_media_url: option.logo_media_url ?? null,
        }
      : null;
  const results = isFull
    ? []
    : (lookup?.[0]?.values ?? []).map(toPick).filter((pick): pick is CollegePick => pick !== null);
  const query = input.trim();
  const hasSomethingToShow =
    query.length >= MIN_SEARCH_LENGTH &&
    (isFull || results.length > 0 || (searchTerm === query && !isFetching));
  const isPopupOpen = open && hasSomethingToShow;
  const removePick = (institutionId: number) =>
    onPickedChange(picked.filter((chosen) => chosen.institution_id !== institutionId));
  const renderChip = (pick: CollegePick) => (
    <CollegeChip
      key={pick.institution_id}
      id={`${idPrefix}-picked-${pick.institution_id}`}
      college={pick}
      onRemove={disabled ? undefined : () => removePick(pick.institution_id)}
    />
  );

  return (
    <Box sx={containerSx}>
      <Box sx={rowSx}>
        <Box sx={[searchBoxSx, ...(Array.isArray(searchSx) ? searchSx : [searchSx])]}>
          <Autocomplete
            id={`${idPrefix}-search`}
            autoHighlight
            multiple
            disabled={disabled}
            disableClearable
            filterSelectedOptions
            forcePopupIcon={false}
            open={isPopupOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            options={[...picked, ...results]}
            value={[...picked]}
            inputValue={input}
            onChange={(_, value, reason) => {
              if (reason === 'removeOption' && !chipsInInput) return;

              onPickedChange(value);
              setInput('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && onSubmit && !isPopupOpen && picked.length > 0) {
                onSubmit();
              }
            }}
            onInputChange={(event, value, reason) => {
              if (reason !== 'reset' || event?.type === 'blur') setInput(value);
            }}
            filterOptions={(options) => options}
            getOptionLabel={(pick) => pick.name}
            getOptionDisabled={(pick) => disableListed && listed.has(pick.institution_id)}
            isOptionEqualToValue={(a, b) => a.institution_id === b.institution_id}
            loading={isFetching}
            noOptionsText={
              (isFull && strings.full) || (isError && strings.lookupFailed) || strings.noMatches
            }
            renderOption={(props, pick) => (
              <Box component="li" {...props} key={pick.institution_id} sx={optionSx}>
                <Box sx={logoSx}>
                  {pick.logo_media_url ? (
                    <Box component="img" src={pick.logo_media_url} alt="" sx={logoImageSx} />
                  ) : (
                    <AppIcon iconName="Building07" diameter="16px" color="Gray/500" />
                  )}
                </Box>
                <Box>
                  <Typography variant="text-sm-regular">{pick.name}</Typography>
                  {listed.has(pick.institution_id) && (
                    <Typography variant="text-xs-regular" component="p" sx={mutedSx}>
                      {strings.onList}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            renderTags={(value) => (chipsInInput ? value.map(renderChip) : null)}
            renderInput={(params) => (
              <TextField
                {...params}
                inputProps={{ ...params.inputProps, 'aria-label': strings.label }}
                placeholder={strings.placeholder}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <Box sx={searchIconSx}>
                        <AppIcon iconName="SearchMd" diameter="20px" color="Gray/500" />
                      </Box>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={autocompleteSx}
          />
        </Box>
        {children}
      </Box>
      {!chipsInInput && picked.length > 0 && (
        <Box id={`${idPrefix}-picked`} sx={pickedSx}>
          {picked.map(renderChip)}
        </Box>
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  minWidth: 0,
});

const rowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-lg'],
  minWidth: 0,
});

const searchBoxSx: SxProps<Theme> = {
  display: 'flex',
  minWidth: 0,
};

const autocompleteSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  },
});

const searchIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  paddingInlineStart: backrs.spacing['spacing-xs'],
});

const optionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const logoSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: backrs.spacing['spacing-3xl'],
  height: backrs.spacing['spacing-3xl'],
});

const logoImageSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};

const pickedSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
